'use client';

import type { FC } from 'react';

import type { GameDto } from '@/queries/games/games.schema';
import { useTranslations } from 'next-intl';

import GameCard from '@/components/entities/games/GameCard';
import { ShowAllButton } from '@/components/shared';
import { SliderWrapper } from '@/components/shared/SliderWrapper';
import { SlotsFilledAliases } from '@/consts/seo/SlotsAliases';
import { AppRoutes } from '@/i18n/constants';
import { isEmpty } from '@/utils/objects.utils';

const FILLED_ALIASES = new Set(SlotsFilledAliases);

function getGamePath(game: GameDto) {
  return FILLED_ALIASES.has(game.alias)
    ? AppRoutes.slotsSlug(game.alias)
    : AppRoutes.gamesId(game.id);
}

interface GameSimilarSlotsProps {
  similarSlotsData?: GameDto[];
  categoryId: string;
}

export const GameSimilarSlots: FC<GameSimilarSlotsProps> = ({ categoryId, similarSlotsData }) => {
  const t = useTranslations('game');
  const similarSlots = similarSlotsData ?? [];

  if (isEmpty(similarSlots)) {
    return null;
  }

  const renderGameCard = (game: GameDto) => (
    <GameCard
      {...game}
      id={game.id}
      showName
      pictureClassName="h-47.5! w-35.5!"
      path={getGamePath(game)}
    >
      {/* TODO: not available now */}
      {/* <RatingStars rating={5} showValue size="sm" /> */}
    </GameCard>
  );

  return (
    <section className="lg:max-w-200 min-w-0 w-full">
      <h3 className="text-xl mb-2.5 leading-[1.4] font-medium text-light">{t('game-details.similar-slots')}</h3>

      <div className="relative">
        <div className="relative -mx-4 lg:-ml-10 lg:-mr-10 min-[1178px]:mr-0 min-[1178px]:ml-0 overflow-hidden lg:overflow-visible">
          <SliderWrapper className="min-[1178px]:overflow-x-visible">
            <div className="flex w-max pl-4 pr-4 gap-[1.4rem] lg:pl-10 lg:pr-10 min-[1178px]:pl-0 min-[1178px]:pr-0">
              {similarSlots.map(game => (
                <div key={game.id} className="min-w-35.5 max-w-35.5 shrink-0">
                  {renderGameCard(game)}
                </div>
              ))}

              <div className="w-35.5! h-full p-1 cursor-pointer flex items-center justify-center shrink-0">
                <ShowAllButton href={AppRoutes.categoryIdPaged(categoryId)} className="h-47.5 w-35.5" />
              </div>
            </div>
          </SliderWrapper>
        </div>
      </div>
    </section>
  );
};
